import React, { useEffect, useState } from 'react';

import { type Variants } from 'framer-motion';

import { YatSprite } from '@pkg/components';

import { useToastStore } from './useToastStore';

import { ToastBorder, ToastContent, ToastIcon, ToastInside, ToastSpinner, ToastText } from './styles';

const toastVariants: Variants = {
    first: {
        y: 0,
        opacity: 1,
        scale: 1,
        zIndex: 3,
        transition: {
            ease: 'anticipate',
            duration: 0.5,
        },
    },
    second: {
        y: -16,
        opacity: 1,
        scale: 0.7,
        zIndex: 2,
        transition: {
            ease: 'linear',
            duration: 0.3,
        },
    },
    third: {
        y: -30,
        opacity: 1,
        scale: 0.4,
        zIndex: 1,
        transition: {
            ease: 'linear',
            duration: 0.3,
        },
    },
    initial: {
        y: '100%',
        opacity: 0,
    },
    exit: {
        y: 0,
        opacity: 0,
        scale: 0,
        transition: {
            ease: 'linear',
            duration: 0.2,
        },
    },
};

export interface ToastProps {
    id?: number | string;
    index?: number;
    emoji?: string;
    message: string;
    timeout?: number;
    delay?: number;
    isHovered?: boolean;
    minimal?: boolean;
    isLoader?: boolean;
}

export const Toast = ({
    id,
    index,
    emoji,
    message,
    timeout = 4500,
    delay = 0,
    isHovered = false,
    minimal = false,
    isLoader = false,
}: ToastProps) => {
    const [show, setShow] = useState(false);

    const { removeToast } = useToastStore();

    useEffect(() => {
        const timer = setTimeout(() => {
            setShow(true);
        }, delay);
        return () => clearTimeout(timer);
    }, [delay]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (show && !isHovered && !isLoader) {
            timer = setTimeout(() => {
                setShow(false);
                removeToast(id || 0);
            }, timeout);
        }
        return () => clearTimeout(timer);
    }, [show, id, timeout, removeToast, isHovered, isLoader]);

    const handleHide = (id: number | string) => {
        setShow(false);
        removeToast(id);
    };

    let positionVariant = 'hidden';
    if (show) {
        if (index === 0) positionVariant = 'first';
        else if (index === 1) positionVariant = 'second';
        else if (index === 2) positionVariant = 'third';
    }

    return (
        <ToastBorder
            $minimal={minimal}
            onClick={() => handleHide(id || 0)}
            initial="initial"
            exit="exit"
            variants={toastVariants}
            animate={positionVariant}
        >
            <ToastContent $hasEmoji={Boolean(emoji)}>
                <ToastInside>
                    {emoji && !isLoader && (
                        <ToastIcon>
                            <YatSprite emojiID={emoji} />
                        </ToastIcon>
                    )}
                    {isLoader && <ToastSpinner />}
                    <ToastText>{message}</ToastText>
                </ToastInside>
            </ToastContent>
        </ToastBorder>
    );
};
